import { Injectable } from '@angular/core';
import { SignalRService } from './signalR.service';
import { StateType, StateValueHistory, SubscriptionType } from '../models';
import { EntityAction, EntityType, MultiEditModel } from '../models/multi-edit.model';
import { DataService } from './data.service';
import { Subject, Subscription, from, of } from 'rxjs';
import { InspectionPluginService } from './plugin.service';
import { BaseService } from './baseService';
import { InspectionPointService } from './point.service';
import { InspectionSectionService } from './section.service';
import { catchError, concatMap, debounceTime, groupBy, map, mergeMap, filter as rxFilter, switchMap, tap } from 'rxjs/operators';
import { InspectionFactoryService } from './inspectionFactory.service';
import { InspectionService } from './inspection.service';
import { SnackBarService } from './snack-bar.service';
import { LanguageService } from './language.service';
import { environment } from '../../../environments/environment';
import { ActionPlanDataService } from './action-plan-data.service';
import { SessionDetectService } from './session-detect.service';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class MultiEditService extends BaseService {
  private sectionUpdateSubject = new Subject<string>();
  private pointUpdateSubject = new Subject<string>();
  private installationUpdateSubject = new Subject<string>();
  private notifySubject$ = new Subject<MultiEditModel>();

  readonly processMap = new Map([
    [`${EntityType.Plugin}${EntityAction.Create}`, this.updatePlugin$],
    [`${EntityType.Plugin}${EntityAction.Update}`, this.updatePlugin$],
    [`${EntityType.Plugin}${EntityAction.Delete}`, this.deletePlugin$],
    [`${EntityType.Point}${EntityAction.Create}`, this.updatePoint$],
    [`${EntityType.Point}${EntityAction.Update}`, this.updatePoint$],
    [`${EntityType.Point}${EntityAction.Delete}`, this.deletePoint$],
    [`${EntityType.Section}${EntityAction.Create}`, this.updateSection$],
    [`${EntityType.Section}${EntityAction.Update}`, this.updateSection$],
    [`${EntityType.Section}${EntityAction.Delete}`, this.deleteSection$],
    [`${EntityType.Folder}${EntityAction.Delete}`, this.deleteFolder$],
    [`${EntityType.Inspection}${EntityAction.OrderItems}`, this.orderItems$],
    [`${EntityType.Inspection}${EntityAction.UpdateProperties}`, this.updateProperties$],
    [`${EntityType.Inspection}${EntityAction.AddNestedInspection}`, this.addNestedInspection$],
    [`${EntityType.Inspection}${EntityAction.DeleteNestedInspection}`, this.deleteNestedInspection$],
    [`${EntityType.Inspection}${EntityAction.Delete}`, this.deleteInspection$],
    [`${EntityType.Inspection}${EntityAction.Download}`, this.downloadInspection$]
  ]);

  private queueSub?: Subscription;
  private inspectionId!: string;

  constructor(
    private router: Router,
    private signalR: SignalRService,
    private dataService: DataService,
    private actionPlanDataService: ActionPlanDataService,
    private pluginService: InspectionPluginService,
    private pointService: InspectionPointService,
    private sectionService: InspectionSectionService,
    private inspectionService: InspectionService,
    private factory: InspectionFactoryService,
    private snackBar: SnackBarService,
    private languages: LanguageService,
    private sessionDetect: SessionDetectService
  ) {
    super();
    this.notifySubject$
      .pipe(
        groupBy((e) => e.changedBy),
        mergeMap((group$) => group$.pipe(debounceTime(1000)))
      )
      .subscribe((e) => this.snackBar.info('multi-edit.notify', 3, { by: e.changedBy }));
  }

  sectionUpdate(id: string) {
    return this.sectionUpdateSubject.asObservable().pipe(rxFilter((sectionId) => sectionId === id));
  }
  pointUpdate(id: string) {
    return this.pointUpdateSubject.asObservable().pipe(rxFilter((pointId) => pointId === id));
  }
  installationUpdate(id: string) {
    return this.installationUpdateSubject.asObservable().pipe(rxFilter((installationId) => installationId === id));
  }

  deactivate() {
    if (this.inspectionId) this.signalR.unsubscribe(this.inspectionId, StateType.Platform);
    this.sessionDetect.deactivate();
    this.queueSub?.unsubscribe();
    this.inspectionId = '';
  }

  activate(inspectionId: string, etag: Date, checkLatestUpdates = false) {
    this.sessionDetect.activateUserDetection(inspectionId);
    if (this.inspectionId === inspectionId) return;

    this.queueSub?.unsubscribe();
    if (this.inspectionId) this.signalR.unsubscribe(this.inspectionId, StateType.Platform);

    this.inspectionId = inspectionId;

    let since = new Date();
    if (checkLatestUpdates) since = etag;

    let isFirst = true;

    this.queueSub = this.signalR
      .subscribeFor(inspectionId, StateType.Platform, {
        replaySince: since,
        subscriptionType: SubscriptionType.ReplayAll
      })
      .pipe(
        concatMap((batch: StateValueHistory[]) => {
          if (isFirst && checkLatestUpdates) {
            isFirst = false;
            if (batch.length > 10) {
              window.location.reload();
              return of(null);
            }
          }

          return from(batch).pipe(
            concatMap((item) =>
              this.process$(item).pipe(
                catchError((err) => {
                  console.error('MultiEdit process error for item', item, err);
                  return of(null);
                })
              )
            )
          );
        })
      )
      .subscribe();
  }

  private updatePlugin$(model: MultiEditModel) {
    return this.pluginService
      .getPluginFromServer(this.inspectionId, model.entityId, {
        ignoreLoader: 'true'
      })
      .pipe(
        rxFilter((plugin) => plugin.status !== -10),
        switchMap((plugin) => this.factory.addServerPlugins(this.inspectionId, plugin.pointId, [plugin]).pipe(map(() => plugin))),
        tap((plugin) => {
          this.dataService.livePluginUpdate(this.inspectionId, plugin);
          this.pointUpdateSubject.next(plugin.pointId);
          this.notify(model, plugin.name);
        })
      );
  }

  private deletePlugin$(model: MultiEditModel) {
    return this.dataService.getPlugin(this.inspectionId, model.entityId).pipe(
      switchMap((plugin) => this.navigate(model, plugin).pipe(map(() => plugin))),
      tap((plugin) => {
        this.notify(model, plugin.name);
        this.dataService.removePlugin(this.inspectionId, model.entityId);
        this.pointUpdateSubject.next(plugin?.pointId);
      })
    );
  }

  private updatePoint$(model: MultiEditModel) {
    return this.pointService
      .getPointFromServer(this.inspectionId, model.entityId, {
        ignoreLoader: 'true'
      })
      .pipe(
        rxFilter((point) => point?.status !== -10),
        tap((point) => {
          this.dataService.updatePoint(this.inspectionId, point);
          this.sectionUpdateSubject.next(point.sectionId);
          this.pointUpdateSubject.next(point.id);
          this.notify(model, point.name);
        })
      );
  }

  private deleteFolder$(model: MultiEditModel) {
    return this.dataService.getFolder(this.inspectionId, model.entityId).pipe(
      switchMap((folder) => this.navigate(model, folder).pipe(map(() => folder))),
      tap((folder) => {
        this.notify(model, folder.name);
      })
    );
  }

  private deletePoint$(model: MultiEditModel) {
    return this.dataService.getPoint(this.inspectionId, model.entityId).pipe(
      switchMap((point) => this.navigate(model, point).pipe(map(() => point))),
      tap((point) => {
        this.notify(model, point.name);
        this.dataService.removePoint(this.inspectionId, model.entityId);
        this.sectionUpdateSubject.next(point?.sectionId);
        this.pointUpdateSubject.next(point?.id);
      })
    );
  }

  private updateSection$(model: MultiEditModel) {
    return this.sectionService
      .getSectionFromServer(this.inspectionId, model.entityId, {
        ignoreLoader: 'true'
      })
      .pipe(
        rxFilter((section) => section?.status !== -10),
        tap((section) => {
          this.dataService.updateSection(this.inspectionId, section);
          this.sectionUpdateSubject.next(section.id);
          this.notify(model, section.name);
        })
      );
  }

  private deleteSection$(model: MultiEditModel) {
    return this.dataService.getSection(this.inspectionId, model.entityId).pipe(
      switchMap((section) => this.navigate(model, section).pipe(map(() => section))),
      tap((section) => {
        this.notify(model, section.name);
        this.dataService.removeSection(this.inspectionId, [model.entityId]);
        this.sectionUpdateSubject.next(section?.id);
      })
    );
  }

  private orderItems$(model: MultiEditModel) {
    return this.inspectionService
      .getSectionItems(model.entityId, {
        ignoreLoader: 'true'
      })
      .pipe(
        tap((inspection) => {
          this.dataService.processSectionItems(this.dataService.getInspection(inspection.id), inspection.sectionsItems);
          this.snackBar.info('multi-edit.order-sections', 3, { by: model.changedBy });
        })
      );
  }

  private updateProperties$(model: MultiEditModel) {
    return this.inspectionService
      .getProperties(model.entityId, {
        ignoreLoader: 'true'
      })
      .pipe(
        tap((inspection) => {
          this.dataService.updateInspectionProperties(inspection);
          this.notify(model, inspection.name);
        })
      );
  }

  private addNestedInspection$(model: MultiEditModel) {
    this.installationUpdateSubject.next(model.entityId);
    this.notify(model, model.entityName);
    return of(null);
  }

  private deleteNestedInspection$(model: MultiEditModel) {
    this.installationUpdateSubject.next(model.entityId);
    this.notify(model, model.entityName);
    return of(null);
  }

  private deleteInspection$(model: MultiEditModel) {
    const inspection = this.dataService.getInspection(model.entityId);
    return this.navigate(model, inspection).pipe(
      tap(() => {
        this.notify(model, inspection.name);
        this.dataService.removeInspection(this.inspectionId);
        if (inspection.actionPlanId) this.actionPlanDataService.removeActionPlan(inspection.actionPlanId);
      })
    );
  }

  private downloadInspection$(model: MultiEditModel) {
    this.router.navigate([environment.applicationRoute, 'inspections', model.entityId]);
    setTimeout(() => location.reload());
  }

  private process$(value: StateValueHistory) {
    try {
      const data: MultiEditModel = JSON.parse(value.valueData);
      if (data.sessionId === this.sessionId) return of(null);

      const key = `${data.entityType}${data.action}`;
      const handler = this.processMap.get(key);
      if (!handler) return of(null);

      // return handler.call(this, data).pipe(tap(() => this.notify(data)));
      return handler.call(this, data);
    } catch (e) {
      console.error('Error parsing multi-edit value:', e);
      return of(null);
    }
  }

  private notify(model: MultiEditModel, name: string) {
    // this.notifySubject$.next(model);
    this.snackBar.clear();
    this.snackBar.info('multi-edit.notification', 3, {
      name,
      action: model.action.toLowerCase(),
      type: model.entityType.toLowerCase(),
      by: model.changedBy
    });
  }

  private navigate(model: MultiEditModel, entity: any) {
    if (!this.router.url.includes(model.entityId)) return of(null);

    const message = this.languages.getNestedValue('multi-edit.redirect', {
      name: entity.name,
      type: model.entityType.toLowerCase()
    });
    if (!confirm(message)) return of(null);

    let commands = [];

    switch (model.entityType) {
      case EntityType.Inspection:
        commands = [environment.applicationRoute, 'inspections'];
        break;
      case EntityType.Section:
        commands = [environment.applicationRoute, 'inspections', this.inspectionId];
        break;
      case EntityType.Folder:
        commands = [environment.applicationRoute, 'inspections', this.inspectionId, 'sections', entity.section?.id];
        break;
      case EntityType.Point:
        commands = [environment.applicationRoute, 'inspections', this.inspectionId, 'sections', entity.sectionId];
        break;
      case EntityType.Plugin:
        commands = entity.pointId
          ? [environment.applicationRoute, 'inspections', this.inspectionId, 'points', entity.pointId]
          : [environment.applicationRoute, 'inspections', this.inspectionId, 'view'];
        break;
      default:
        return of(null);
    }
    return from(this.router.navigate(commands));
  }
}
